"use client"

import * as React from "react"
import { Slot, Tooltip as TooltipPrimitive } from "radix-ui"

import { cn } from "@/lib/utils"

// `disableHoverableContent` defaults to true: tooltips close as soon as the
// cursor leaves the trigger, even if it then moves over the tooltip content.
// The Radix default (false) leaves tooltips lingering whenever the cursor
// drifts into the floating panel, which feels stuck.
function TooltipProvider({
  delayDuration = 0,
  disableHoverableContent = true,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>): React.JSX.Element {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      disableHoverableContent={disableHoverableContent}
      {...props}
    />
  );
}

// A tooltip must never outlive the hover that opened it. Radix closes on
// pointerleave and blur, which covers a pointer that MOVES away — but not a
// pointer that stops being over the trigger without moving:
//
//   - A context menu on the same trigger restores focus to it when it closes,
//     and a focused trigger opens the tooltip. Pick a menu item that opens a
//     dialog and the tooltip is left stranded on top of that dialog, with no
//     pointer on the trigger to dismiss it (the objects gallery: every tile
//     carries both an info tooltip and the known-object context menu).
//   - Any modal Radix layer sets `pointer-events: none` on <body>, so the
//     trigger silently stops being hovered — no pointerleave is ever fired.
//
// So the premise is re-checked while the tooltip is open: the trigger must
// still match :hover, or have a focus that a key moved (the keyboard path,
// which has no hover). Anything else closes it. The same check gates OPENING,
// so a focus-restore with the pointer elsewhere cannot raise a tooltip at all.
// One `matches()` per interval, only while open, and Radix's provider keeps
// at most one tooltip open at a time.
const HOVER_CHECK_MS = 150;

// Chrome's own :focus-visible also matches a trigger that a menu gives the
// focus back to after a mouse pick. The keyboard mark of app/keyboardFocus.ts
// does not, as for the focus ring.
const KEYBOARD_FOCUS_SELECTOR = "[data-keyboard-focus] :focus-visible";

// Lets Tooltip observe the DOM node its own TooltipTrigger rendered.
const TooltipTriggerRefContext =
  React.createContext<React.RefObject<HTMLElement | null> | null>(null);

/** Whether `el` is genuinely under the pointer, or focused from a keyboard. */
function isTriggerEngaged(el: HTMLElement | null): boolean {
  if (!el) {
    return true; // no node to judge by — leave Radix's own logic alone
  }
  try {
    return el.matches(':hover') || el.matches(KEYBOARD_FOCUS_SELECTOR);
  } catch {
    return true; // exotic engine without :focus-visible — don't fight Radix
  }
}

function Tooltip({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>): React.JSX.Element {
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  // `open` given = the caller drives it (StickyRangeTooltip); we only forward.
  const controlled = open !== undefined;
  const isOpen = controlled ? open : uncontrolledOpen;

  const handleOpenChange = React.useCallback((next: boolean) => {
    if (next && !isTriggerEngaged(triggerRef.current)) {
      return;
    }
    if (!controlled) {
      setUncontrolledOpen(next);
    }
    onOpenChange?.(next);
  }, [controlled, onOpenChange]);

  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    const close = () => {
      if (!controlled) {
        setUncontrolledOpen(false);
      }
      onOpenChange?.(false);
    };
    const checkStranded = () => {
      if (!isTriggerEngaged(triggerRef.current)) {
        close();
      }
    };
    const timer = setInterval(checkStranded, HOVER_CHECK_MS);
    // Alt-tab / minimise leaves the pointer nowhere at all.
    window.addEventListener('blur', close);
    document.addEventListener('visibilitychange', close);
    return () => {
      clearInterval(timer);
      window.removeEventListener('blur', close);
      document.removeEventListener('visibilitychange', close);
    };
  }, [isOpen, controlled, onOpenChange]);

  // Each tooltip carries its own provider, as upstream shadcn does: a control
  // with a tooltip also renders on the pages and in the tests outside the shell.
  return (
    <TooltipProvider>
      <TooltipTriggerRefContext.Provider value={triggerRef}>
        <TooltipPrimitive.Root
          data-slot="tooltip"
          open={isOpen}
          onOpenChange={handleOpenChange}
          {...props}
        />
      </TooltipTriggerRefContext.Provider>
    </TooltipProvider>
  );
}

// Mouse moves that a tooltip trigger nearer the pointer has already taken.
const claimedMoves = new WeakSet<Event>();

type TriggerProps = React.ComponentProps<'button'> & { 'data-state'?: string };

// Sits between Radix's trigger and the child, and takes three things off what
// Radix hands down:
// - Its `data-state` (closed, delayed-open). Spread onto a Radix control — a
//   Switch, a tab, a menu trigger — it would replace the state that control
//   styles itself by.
// - A mouse move already taken by a nested trigger. Opening this tooltip would
//   close that one, so the tooltip nearest the pointer wins.
// - Every handler, while `passive`: a tooltip with nothing to say must not
//   start opening, because the start alone closes the other tooltips.
function WithoutTooltipState({
  asChild,
  passive = false,
  ...props
}: TriggerProps & { asChild?: boolean; passive?: boolean }): React.JSX.Element {
  const {
    'data-state': _tooltipState,
    onPointerMove, onPointerLeave, onPointerDown, onClick, onFocus, onBlur,
    ...rest
  } = props;
  const Comp = asChild ? Slot.Root : 'button';
  if (passive) {
    return <Comp {...rest} />;
  }
  return (
    <Comp
      {...rest}
      onPointerLeave={onPointerLeave}
      onPointerDown={onPointerDown}
      onClick={onClick}
      onFocus={onFocus}
      onBlur={onBlur}
      onPointerMove={(event) => {
        if (event.pointerType === 'mouse') {
          if (claimedMoves.has(event.nativeEvent)) {
            return;
          }
          claimedMoves.add(event.nativeEvent);
        }
        onPointerMove?.(event);
      }}
    />
  );
}

// The trigger is routinely composed with another Radix trigger via nested
// `asChild` (objects tile = context menu + tooltip on one element), and that
// outer trigger hands its ref down through this component, so the node goes
// to that ref and to the hover ref both.
function TooltipTrigger({
  asChild,
  passive,
  children,
  ref: forwardedRef,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger> & { passive?: boolean }): React.JSX.Element {
  const hoverRef = React.useContext(TooltipTriggerRefContext);
  const ref = React.useCallback((node: HTMLButtonElement | null) => {
    if (hoverRef) {
      hoverRef.current = node;
    }
    if (typeof forwardedRef === 'function') {
      forwardedRef(node);
    } else if (forwardedRef) {
      forwardedRef.current = node;
    }
  }, [hoverRef, forwardedRef]);
  return (
    <TooltipPrimitive.Trigger data-slot="tooltip-trigger" ref={ref} asChild {...props}>
      <WithoutTooltipState asChild={asChild} passive={passive}>{children}</WithoutTooltipState>
    </TooltipPrimitive.Trigger>
  );
}

// A tooltip never takes the pointer. A tooltip that covers its trigger would
// take the hover from it, and the tooltip would close and open in a loop. The
// Radix wrapper around the content is a box of its own, so `[:has(>&)]` puts
// `pointer-events: none` on the wrapper, and the content inherits it.
function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>): React.JSX.Element {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "[:has(>&)]:pointer-events-none",
          "z-50 w-fit origin-(--radix-tooltip-content-transform-origin) animate-in rounded-md bg-occ-background-inverted px-3 py-1.5 text-xs text-balance text-occ-foreground-inverted fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 [&>span]:overflow-hidden",
          className
        )}
        {...props}>
        {children}
        <TooltipPrimitive.Arrow
          className="relative -z-10 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px] bg-occ-background-inverted fill-occ-background-inverted" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider }
